<template>
  <!--
    Şirket seçimi (CorporateSelect)

    Bir kurumun altında birden fazla şirket olabilir.
    Örnek: AZCON Corporate Academy altında "Dənizçi MTK".

    prefill-from-self açıkken (getAssignedCorporateAccess servisi):
    - Rol company_admin değilse ve kullanıcı bir şirkete atanmışsa
      → Atandığı şirket seçili gelir, alan kilitlenir, diğer şirketlere erişemez
    - company_admin ise tüm şirketleri serbestçe seçebilir

    Liste sayfaları ilk yüklemede aynı servisi kullanmalı ki
    tüm şirketlerin verisi gelmesin.
  -->
  <div class="corporate-select">
    <input v-if="isDisabled"
           :value="lockedCorporateName"
           class="corporate-select__locked"
           disabled
           readonly
           type="text"/>

    <multiselect v-else
                 v-model="selected"
                 :allow-empty="true"
                 :clear-on-select="false"
                 :close-on-select="!multiple"
                 :deselect-label="$t('general.deselect')"
                 :multiple="multiple"
                 :options="corporateOptions"
                 :placeholder="$t('general.select')"
                 :preserve-search="true"
                 :searchable="true"
                 :select-label="$t('general.select')"
                 :selected-label="$t('general.selected')"
                 :tag-placeholder="$t('general.select')"
                 label="name"
                 track-by="id">

      <template slot="option" slot-scope="{ option }">
        {{ option.name }}
      </template>
    </multiselect>
  </div>
</template>

<script>
import store from "@/core/services";
import { getAssignedCorporateAccess } from "@/core/services/userCorporateAccess.service";

import corporate_module, {
  BASE_URL as CORPORATE_BASE_URL,
  GET_ITEMS as GET_CORPORATES,
  ITEMS as CORPORATES,
  MODULE_NAME as CORPORATE_MODULE,
} from "@/core/services/store/corporate.module";

export default {
  name: "CorporateSelect",
  props: {
    value: {
      type: [Array, Object, Number],
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // true: atanmış şirketi otomatik seç + kilitle (company_admin hariç)
    prefillFromSelf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Kullanıcının atandığı şirket (servisten gelir); doluysa dropdown alanı kilitlenir
      assignedCorporate: null,
    };
  },
  beforeCreate() {
    if (!(store && store.state && store.state[CORPORATE_MODULE])) {
      store.registerModule(CORPORATE_MODULE, corporate_module);
    }
  },
  computed: {
    isDisabled() {
      return this.disabled || !!this.assignedCorporate;
    },
    // Kilitli durumda input'ta gösterilecek şirket adı
    lockedCorporateName() {
      return this.assignedCorporate?.name || this.selectedCorporates[0]?.name || '';
    },
    selected: {
      get() {
        return this.value;
      },
      set(val) {
        if (this.isDisabled) return;
        this.$emit("input", val);
      },
    },
    corporates() {
      return store.getters[CORPORATE_MODULE + "/" + CORPORATES];
    },
    // Dropdown'da listelenecek şirketler; seçili olup listede olmayanlar başa eklenir
    corporateOptions() {
      if (!this.corporates || !Array.isArray(this.corporates)) {
        return this.selectedCorporates;
      }
      const options = this.corporates.map((item) => ({ id: item.id, name: item.name }));
      this.selectedCorporates.forEach((selected) => {
        if (selected?.id != null && !options.some((option) => option.id === selected.id)) {
          options.unshift(selected);
        }
      });
      return options;
    },
    // v-model değerini her zaman [{id, name}] dizisine çevirir
    selectedCorporates() {
      if (Array.isArray(this.value)) {
        return this.value.filter((item) => item && item.id != null);
      }
      if (this.value && typeof this.value === 'object' && this.value.id != null) {
        return [this.value];
      }
      return [];
    },
  },
  watch: {
    companyId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchCorporates(id);
      },
    },
    value: {
      handler() {
        // Parent reset/temizleme yapsa bile atanmış şirket seçili kalsın
        if (this.assignedCorporate && this.selectedCorporates.length === 0) {
          this.emitCorporateAsSelected(this.assignedCorporate);
        }
      },
    },
  },
  mounted() {
    if (this.prefillFromSelf) {
      this.prefillAssignedCorporate();
    }
  },
  methods: {
    fetchCorporates(companyId) {
      store.dispatch(CORPORATE_MODULE + "/" + GET_CORPORATES, {
        url: CORPORATE_BASE_URL,
        filters: { company_id: companyId },
      });
    },
    // Şirketi v-model'e seçili olarak yazar (multiple ise dizi olarak)
    emitCorporateAsSelected(corporate) {
      this.$emit("input", this.multiple ? [corporate] : corporate);
    },
    // Kullanıcı bir şirkete kilitliyse: o şirketi seçer ve alanı kilitler
    async prefillAssignedCorporate() {
      const corporateAccess = await getAssignedCorporateAccess();
      this.assignedCorporate = corporateAccess.isLockedToAssignedCorporate
          ? corporateAccess.assignedCorporate
          : null;

      if (this.assignedCorporate) {
        this.emitCorporateAsSelected(this.assignedCorporate);
      }
    },
  },
};
</script>

<style scoped>
.corporate-select {
  width: 100%;
}

.corporate-select__locked {
  display: block;
  width: 100%;
  min-height: 40px;
  height: 40px;
  margin: 0;
  padding: 8px 12px;
  box-sizing: border-box;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  background-color: #f3f3f3;
  color: #35495e;
  font-size: 14px;
  line-height: 24px;
  cursor: not-allowed;
  opacity: 1;
}
</style>
